import React, {useState} from 'react'
import Head from 'next/head'
import Image from 'next/image'
import alerts from '@/config/alerts.json'
import ProductDisclosure from '@/components/Products/ProductFAQDisclosure'
import {GetStaticProps} from 'next'
import {ParsedUrlQuery} from 'querystring'
import GetSlugs from '@/services/api/products/GetSlugs'
import GetBySlug from '@/services/api/products/GetBySlug'
import {useRouter} from 'next/router'
import {PersonalizeURLBuilder} from '@/utils/URLBuilder'
import Link from 'next/link'
import ProductSlider from '@/components/Products/ProductSlider'
import toast from 'react-hot-toast'
import StarRatings from 'react-star-ratings'
import {useMutation, useQuery, useQueryClient} from 'react-query'
import {
  GetReviews,
  ReviewDelete,
  ReviewPost,
  ReviewUpdate,
} from '@/services/api/review'
import Button from '@/components/Button'
import useAuth from '@/hooks/useAuth'
import GetRelatedProducts from '@/services/api/products/GetRelatedProducts'
import ProductItem from '@/components/Products/ProductItem'
import {
  CheckInWishlist,
  GetWishlist,
  WishlistAdd,
  WishlistDelete,
} from '@/services/api/wishlist'
import {GetSettings} from '@/services/api/settings'
import {GetDocumentInputs} from '@/services/api/personalize/GetDocuments'
interface IParams extends ParsedUrlQuery {
  slug: string
}

export const getStaticPaths = async () => {
  try {
    const arr: string[] = await GetSlugs()
    const paths = arr.map(slug => {
      return {
        params: {slug},
      }
    })
    return {paths, fallback: 'blocking'}
  } catch (error) {
    console.error('Error getting slugs:', error)
    return {paths: [], fallback: 'blocking'} 
  }
}
export const getStaticProps: GetStaticProps = async context => {
  const {slug} = context.params as IParams; // no longer causes error
  try {
    const [product, settings, inputs] = await Promise.all([
      GetBySlug(slug),
      GetSettings(),
      GetDocumentInputs(slug),
    ]);
    return {props: {product, settings, inputs}, revalidate: 10};
  } catch (error) {
    console.error('Error fetching product data:', error);
    return {
      props: {
        product: null,
        settings: null,
        inputs: null,
      },
      revalidate: 10,
    };
  }
}

interface Props {
  product: any
  settings: any
  inputs: any
}
export default function Product({product, settings, inputs}: Props) {
  const router = useRouter()  
  const name: string =
    router.query.name != undefined
      ? (router.query.name as string)
      : product.replace_name
  const gender =
    router.query.gender != undefined ? (router.query.gender as string) : ''

  const [reviewStar, setReviewStar] = React.useState(0)
  const [review, setReview] = React.useState('')
  const [isEditing, setIsEditing] = React.useState<undefined | number>(
    undefined,
  )

  const {data, isLoading} = useQuery(['reviews', product.id], GetReviews)
  const {user} = useAuth('none')
  const mutation = useMutation(ReviewPost)
  const DeleteMutation = useMutation(ReviewDelete)
  const UpdateMutation = useMutation(ReviewUpdate)

  const {data: wishlist} = useQuery(['wishlist'], GetWishlist)
  const WishlistAddMutation = useMutation(WishlistAdd)
  const DeleteWishlistMutation = useMutation(WishlistDelete)

  const queryClient = useQueryClient()
  const {data: related_products} = useQuery(
    [
      'related_products',
      {
        category_id: product?.categories?.map((cat: any) => cat['id']).toString() || '',
        product_id: product?.id,
        gender: gender,
      },
    ],
    GetRelatedProducts,
  )

  const BuildInputValues = () => {
    const build_inputs: {[key: string]: string} = {}
    if (!inputs) return build_inputs;
    
    inputs.forEach((input: string, index: number) => {
      if (input == 'name') {
        build_inputs[input] = name
      } else if (input === 'first_name') {
        build_inputs[input] = name.split(' ')[0]
      } else if (input === 'init') {
        build_inputs[input] = name.charAt(0)
      } else if (input === 'age') {
        build_inputs[input] = '8'
      }
    })
    return build_inputs
  }
  const [wordCount, setWordCount] = useState(0)
  const [charCount, setCharCount] = useState(0)

  React.useEffect(() => {
    const words = review.split(' ')
    let wordCount = 0
    words.forEach((word: string) => {
      if (word.trim() !== '') {
        wordCount++
      }
    })
    setWordCount(wordCount)
    setCharCount(review.length)
  }, [review])

  const handleCreate = () => {
    mutation.mutate(
      {id: product.id, data: {stars: reviewStar, review}},
      {
        onSuccess() {
          queryClient.invalidateQueries(['reviews'])
          setReview('')
          setReviewStar(0)
          toast.success(alerts.review_added, {duration: 5000})
        },
        onError(error: any) {
          toast.error(error.response.data.message, {
            duration: 4000,
            id: 'Validation Error',
          })
        },
      },
    )
  }

  const handleUpdate = () => {
    UpdateMutation.mutate(
      {id: isEditing, data: {stars: reviewStar, review}},
      {
        onSuccess() {
          queryClient.invalidateQueries(['reviews'])
          setReview('')
          setReviewStar(0)
          setIsEditing(undefined)
          toast.success(alerts.review_updated, {duration: 5000})
        },
        onError(error: any) {
          toast.error(error.response.data.message, {
            duration: 4000,
            id: 'Validation Error',
          })
        },
      },
    )
  }
  const deleteReview = (id: number) => {
    DeleteMutation.mutate(id, {
      onSuccess() {
        queryClient.invalidateQueries(['reviews'])
      },
    })
  }
  const editReview = (id: number, stars: number, review: string) => {
    setReviewStar(stars)
    setReview(review)
    setIsEditing(id)
  }

  const addToWishlist = (id: number) => {
    if (!user) {
      toast.error(alerts.login_wishlist)
      return
    }
    WishlistAddMutation.mutate(id, {
      onSuccess() {
        queryClient.invalidateQueries(['wishlist'])
        queryClient.invalidateQueries(['check_in_wishlist'])
        toast.success(alerts.wishlist_add, {duration: 5000})
      },
      onError(error: any) {
        toast.error(error.response.data.message, {
          duration: 4000,
          id: 'Validation Error',
        })
      },
    })
  }

  const WishlistDel = (id: number) => {
    DeleteWishlistMutation.mutate(id, {
      onSuccess() {
        queryClient.invalidateQueries(['wishlist'])
        queryClient.invalidateQueries(['check_in_wishlist'])
        toast.error(alerts.wishlist_remove, {duration: 5000})
      },
      onError(error: any) {
        toast.error(error.response.data.message, {
          duration: 4000,
          id: 'Validation Error',
        })
      },
    })
  }

  const {data: check_in_wishlist} = useQuery(
    ['check_in_wishlist', product?.id],
    () => product?.id ? CheckInWishlist(product.id) : null,
    {
      enabled: !!product?.id
    }
  )

  // If product is null or undefined, show a loading state or error message
  if (!product) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">المنتج غير متوفر</h1>
          <p>عذراً، لم يتم العثور على المنتج المطلوب.</p>
          <Link href="/products" className="text-blue-500 hover:underline mt-4 inline-block">
            العودة إلى صفحة المنتجات
          </Link>
        </div>
      </div>
    )
  }
  return (
    <>
      <Head>
        <title>
          {product?.demo_name ? product.demo_name.replace('{basmti}', name ?? product.replace_name) : 'منتج - Basmti'}
        </title>
        <meta name="description" content="Basmti Project" />
      </Head>
      <main className="bg-gray-100 pb-24 pt-8">
        <div className="breadcrumbs container-fluid">
          <Link href="/" className="text-gray-400">
            الرئيسية
          </Link>
          <span className="text-gray-400 mx-2">/</span>
          <Link href="/products" className="text-gray-400">
            كتب شخصية للأطفال
          </Link>
          <span className="text-gray-400 mx-2">/</span>
          {router.isReady && product?.demo_name && (
            <a href="#" onClick={e => e.preventDefault()}>
              {product.demo_name.replace(
                '{basmti}',
                name ?? product.replace_name,
              )}
            </a>
          )}
        </div>
        <div className="product-page mt-4">
          <div className="lg:max-w-5xl xl:max-w-[60rem] mx-auto">
            <div className="product-content container  lg:grid lg:grid-cols-2 lg:gap-4">
              <div className="flex items-center flex-wrap mob lg:hidden">
                {product.has_sale && (
                  <div>
                    <span className="mob-only product-discount  bg-red text-white py-2 px-5 rounded-lg">
                      خصم %{product.discount_percentage}
                    </span>
                  </div>
                )}

                {product.tags.map((tag: any, index: number) => {
                  return (
                    <div key={index} className={`mr-2`}>
                      <span className="product-discount desk hidden lg:inline-block bg-red text-white py-2 px-5 rounded-lg">
                        {tag.name}
                      </span>
                    </div>
                  )
                })}
              </div>
              <div className="mob-only product-title mob lg:hidden mt-6 text-xl text-primary ">
                {product.demo_name.replace(
                  '{basmti}',
                  name ?? product.replace_name,
                )}
              </div>
              <div className="overflow-hidden product-image mt-6 w-full">
                <ProductSlider images={product.images} />
              </div>
              <div className="product-description lg:flex lg:flex-col lg:justify-between mt-6 text-text-color">
                <div>
                  <div className="flex items-center">
                    {product.has_sale && (
                      <div>
                        <span className="product-discount desk hidden lg:inline-block bg-red text-white py-2 px-5 rounded-lg">
                          خصم %{product.discount_percentage}
                        </span>
                      </div>
                    )}
                    {product.tags.map((tag: any, index: number) => {
                      return (
                        <div key={index} className={`mr-2`}>
                          <span className="product-discount desk hidden lg:inline-block bg-red text-white py-2 px-5 rounded-lg">
                            {tag.name}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                  {router.isReady && (
                    <div className="product-title desk hidden lg:block mt-6 text-3xl font-bold text-primary">
                      {product.demo_name.replace(
                        '{basmti}',
                        name ?? product.replace_name,
                      )}
                    </div>
                  )}

                  <div
                    className="lg:mt-6 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: product.description.replace(
                        new RegExp('{basmti}', 'g'),
                        name ?? product.replace_name,
                      ),
                    }}></div>
                </div>
                <div>
                  <div className="pricing flex items-center mt-6">
                    <h2 className="text-black text-3xl lg:text-4xl font-bold">
                      <span>₪</span>
                      {product?.front_price}
                    </h2>
                    {product.has_sale && (
                      <h3 className="old-price text-red mr-4 line-through text-xl">
                        <span>₪</span>
                        {product?.price}
                      </h3>
                    )}
                  </div>
                  <div className="product-actions flex mt-3">
                    <Link
                      className="text-primary font-bold py-3 bg-success block text-center rounded-md w-full"
                      href={PersonalizeURLBuilder(
                        BuildInputValues(),
                        gender == ''
                          ? product?.has_male
                            ? 'Male'
                            : 'Female'
                          : gender,
                        product.slug,
                      )}>
                      قم بإنشاء كتابك
                    </Link>

                    {check_in_wishlist && (
                      <a
                        href="#"
                        onClick={e => {
                          e.preventDefault()
                          WishlistDel(product.id)
                        }}
                        className="border border-primary rounded-lg mr-4 flex items-center justify-center p-3">
                        <svg
                          width="23"
                          height="24"
                          viewBox="0 0 23 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M20.1357 24C19.7657 23.999 19.3996 23.9247 19.0586 23.7813C18.7175 23.638 18.4083 23.4285 18.1487 23.165L11.9987 17.051L5.84866 23.169C5.45369 23.5697 4.94715 23.8422 4.39508 23.9508C3.843 24.0594 3.27101 23.9992 2.75366 23.778C2.23118 23.5678 1.7842 23.205 1.47112 22.7369C1.15803 22.2688 0.993376 21.7171 0.998658 21.154V5C0.998658 3.67392 1.52544 2.40215 2.46312 1.46447C3.40081 0.526784 4.67258 0 5.99866 0L17.9987 0C18.6553 0 19.3054 0.129329 19.9121 0.380602C20.5187 0.631876 21.0699 1.00017 21.5342 1.46447C21.9985 1.92876 22.3668 2.47996 22.618 3.08658C22.8693 3.69321 22.9987 4.34339 22.9987 5V21.154C23.0043 21.7167 22.8402 22.268 22.5278 22.7361C22.2155 23.2041 21.7694 23.5672 21.2477 23.778C20.8955 23.9253 20.5174 24.0008 20.1357 24Z"
                            fill="#00214D"
                          />
                        </svg>
                      </a>
                    )}
                    {!check_in_wishlist && (
                      <a
                        href="#"
                        onClick={e => {
                          e.preventDefault()
                          addToWishlist(product.id)
                        }}
                        className="border border-primary rounded-lg mr-4 flex items-center justify-center p-3">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <g clipPath="url(#clip0_919_2605)">
                            <path
                              d="M20.1371 24C19.7672 23.999 19.4011 23.9247 19.0601 23.7813C18.719 23.638 18.4097 23.4285 18.1501 23.165L12.0001 17.051L5.85012 23.169C5.45515 23.5697 4.94861 23.8422 4.39654 23.9508C3.84447 24.0594 3.27247 23.9992 2.75512 23.778C2.23264 23.5678 1.78567 23.205 1.47258 22.7369C1.15949 22.2688 0.994841 21.7171 1.00012 21.154V5C1.00012 3.67392 1.52691 2.40215 2.46459 1.46447C3.40227 0.526784 4.67404 0 6.00012 0L18.0001 0C18.6567 0 19.3069 0.129329 19.9135 0.380602C20.5202 0.631876 21.0714 1.00017 21.5357 1.46447C22 1.92876 22.3683 2.47996 22.6195 3.08658C22.8708 3.69321 23.0001 4.34339 23.0001 5V21.154C23.0057 21.7167 22.8417 22.268 22.5293 22.7361C22.217 23.2041 21.7709 23.5672 21.2491 23.778C20.8969 23.9253 20.5189 24.0008 20.1371 24ZM6.00012 2C5.20447 2 4.44141 2.31607 3.8788 2.87868C3.31619 3.44129 3.00012 4.20435 3.00012 5V21.154C2.99976 21.3206 3.04879 21.4836 3.14102 21.6224C3.23325 21.7612 3.36455 21.8695 3.51831 21.9337C3.67208 21.9979 3.84143 22.0151 4.00496 21.9831C4.1685 21.9512 4.31888 21.8714 4.43712 21.754L11.3001 14.933C11.4875 14.7468 11.7409 14.6422 12.0051 14.6422C12.2693 14.6422 12.5228 14.7468 12.7101 14.933L19.5651 21.752C19.6834 21.8694 19.8338 21.9492 19.9973 21.9811C20.1608 22.0131 20.3302 21.9959 20.4839 21.9317C20.6377 21.8675 20.769 21.7592 20.8612 21.6204C20.9535 21.4816 21.0025 21.3186 21.0021 21.152V5C21.0021 4.20435 20.6861 3.44129 20.1234 2.87868C19.5608 2.31607 18.7978 2 18.0021 2H6.00012Z"
                              fill="#00214D"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_919_2605">
                              <rect width="24" height="24" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="container mx-auto faq mt-6 lg:mt-8">
              {settings.faqs.map((faq: any, index: number) => {
                return <ProductDisclosure key={index} faq={faq} />
              })}
            </div>
          </div>
          <div className="product-reviews py-12 mt-10 bg-white">
            <div className="lg:max-w-5xl xl:max-w-6xl mx-auto">
              <h1 className="text-success text-3xl font-bold">
                التعليقات على الكتاب
              </h1>
              <div className="product-review-list mt-8">
                {data?.map((item: any, index: number) => {
                  return (
                    <div
                      key={index}
                      className={`product-review ${
                        user?.id == item.user_id ? 'bg-gray-100' : ''
                      } flex items-center justify-between px-4 py-2`}>
                      <div className="flex items-center">
                        <div className="product-img flex-shrink-0">
                          <Image
                            src={item.profile_image}
                            alt={item.product_name}
                            width={50}
                            height={50}
                            className="object-cover rounded-full"
                          />
                          <h2 className="font-semibold text-primary mt-2">
                            {item.name}
                          </h2>
                          <svg
                            width="112"
                            height="16"
                            className="mt-2"
                            viewBox="0 0 112 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M15.4829 5.63406L10.6864 4.90408L8.54222 0.352135C8.48365 0.227506 8.38731 0.126615 8.26829 0.0652895C7.96981 -0.0890138 7.6071 0.0395723 7.45786 0.352135L5.3137 4.90408L0.517213 5.63406C0.384974 5.65384 0.26407 5.71912 0.171503 5.81803C0.0595945 5.93848 -0.0020722 6.10053 5.31712e-05 6.26856C0.00217854 6.4366 0.0679221 6.59688 0.182838 6.71418L3.65316 10.2572L2.83328 15.2602C2.81405 15.3766 2.82635 15.4963 2.86878 15.6057C2.91121 15.7151 2.98207 15.8099 3.07333 15.8793C3.16459 15.9488 3.27259 15.99 3.38509 15.9984C3.4976 16.0068 3.6101 15.982 3.70983 15.9269L8.00004 13.5648L12.2902 15.9269C12.4074 15.9922 12.5434 16.0139 12.6737 15.9902C13.0024 15.9308 13.2235 15.6044 13.1668 15.2602L12.3469 10.2572L15.8172 6.71418C15.9117 6.61724 15.974 6.49064 15.9929 6.35216C16.0439 6.00597 15.8135 5.68549 15.4829 5.63406Z"
                              className={`${
                                item.stars > 0
                                  ? 'fill-[#FBDD37]'
                                  : 'fill-[#CAD3E2]'
                              }`}
                            />
                            <path
                              d="M39.4829 5.63406L34.6864 4.90408L32.5422 0.352135C32.4837 0.227506 32.3873 0.126615 32.2683 0.0652895C31.9698 -0.0890138 31.6071 0.0395723 31.4579 0.352135L29.3137 4.90408L24.5172 5.63406C24.385 5.65384 24.2641 5.71912 24.1715 5.81803C24.0596 5.93848 23.9979 6.10053 24.0001 6.26856C24.0022 6.4366 24.0679 6.59688 24.1828 6.71418L27.6532 10.2572L26.8333 15.2602C26.8141 15.3766 26.8264 15.4963 26.8688 15.6057C26.9112 15.7151 26.9821 15.8099 27.0733 15.8793C27.1646 15.9488 27.2726 15.99 27.3851 15.9984C27.4976 16.0068 27.6101 15.982 27.7098 15.9269L32 13.5648L36.2902 15.9269C36.4074 15.9922 36.5434 16.0139 36.6737 15.9902C37.0024 15.9308 37.2235 15.6044 37.1668 15.2602L36.3469 10.2572L39.8172 6.71418C39.9117 6.61724 39.974 6.49064 39.9929 6.35216C40.0439 6.00597 39.8135 5.68549 39.4829 5.63406Z"
                              className={`${
                                item.stars > 1
                                  ? 'fill-[#FBDD37]'
                                  : 'fill-[#CAD3E2]'
                              }`}
                            />
                            <path
                              d="M63.4829 5.63406L58.6864 4.90408L56.5422 0.352135C56.4837 0.227506 56.3873 0.126615 56.2683 0.0652895C55.9698 -0.0890138 55.6071 0.0395723 55.4579 0.352135L53.3137 4.90408L48.5172 5.63406C48.385 5.65384 48.2641 5.71912 48.1715 5.81803C48.0596 5.93848 47.9979 6.10053 48.0001 6.26856C48.0022 6.4366 48.0679 6.59688 48.1828 6.71418L51.6532 10.2572L50.8333 15.2602C50.8141 15.3766 50.8264 15.4963 50.8688 15.6057C50.9112 15.7151 50.9821 15.8099 51.0733 15.8793C51.1646 15.9488 51.2726 15.99 51.3851 15.9984C51.4976 16.0068 51.6101 15.982 51.7098 15.9269L56 13.5648L60.2902 15.9269C60.4074 15.9922 60.5434 16.0139 60.6737 15.9902C61.0024 15.9308 61.2235 15.6044 61.1668 15.2602L60.3469 10.2572L63.8172 6.71418C63.9117 6.61724 63.974 6.49064 63.9929 6.35216C64.0439 6.00597 63.8135 5.68549 63.4829 5.63406Z"
                              className={`${
                                item.stars > 2
                                  ? 'fill-[#FBDD37]'
                                  : 'fill-[#CAD3E2]'
                              }`}
                            />
                            <path
                              d="M87.4829 5.63406L82.6864 4.90408L80.5422 0.352135C80.4837 0.227506 80.3873 0.126615 80.2683 0.0652895C79.9698 -0.0890138 79.6071 0.0395723 79.4579 0.352135L77.3137 4.90408L72.5172 5.63406C72.385 5.65384 72.2641 5.71912 72.1715 5.81803C72.0596 5.93848 71.9979 6.10053 72.0001 6.26856C72.0022 6.4366 72.0679 6.59688 72.1828 6.71418L75.6532 10.2572L74.8333 15.2602C74.8141 15.3766 74.8264 15.4963 74.8688 15.6057C74.9112 15.7151 74.9821 15.8099 75.0733 15.8793C75.1646 15.9488 75.2726 15.99 75.3851 15.9984C75.4976 16.0068 75.6101 15.982 75.7098 15.9269L80 13.5648L84.2902 15.9269C84.4074 15.9922 84.5434 16.0139 84.6737 15.9902C85.0024 15.9308 85.2235 15.6044 85.1668 15.2602L84.3469 10.2572L87.8172 6.71418C87.9117 6.61724 87.974 6.49064 87.9929 6.35216C88.0439 6.00597 87.8135 5.68549 87.4829 5.63406Z"
                              className={`${
                                item.stars > 3
                                  ? 'fill-[#FBDD37]'
                                  : 'fill-[#CAD3E2]'
                              }`}
                            />
                            <path
                              d="M111.483 5.63406L106.686 4.90408L104.542 0.352135C104.484 0.227506 104.387 0.126615 104.268 0.0652895C103.97 -0.0890138 103.607 0.0395723 103.458 0.352135L101.314 4.90408L96.5172 5.63406C96.385 5.65384 96.2641 5.71912 96.1715 5.81803C96.0596 5.93848 95.9979 6.10053 96.0001 6.26856C96.0022 6.4366 96.0679 6.59688 96.1828 6.71418L99.6532 10.2572L98.8333 15.2602C98.8141 15.3766 98.8264 15.4963 98.8688 15.6057C98.9112 15.7151 98.9821 15.8099 99.0733 15.8793C99.1646 15.9488 99.2726 15.99 99.3851 15.9984C99.4976 16.0068 99.6101 15.982 99.7098 15.9269L104 13.5648L108.29 15.9269C108.407 15.9922 108.543 16.0139 108.674 15.9902C109.002 15.9308 109.223 15.6044 109.167 15.2602L108.347 10.2572L111.817 6.71418C111.912 6.61724 111.974 6.49064 111.993 6.35216C112.044 6.00597 111.813 5.68549 111.483 5.63406Z"
                              className={`${
                                item.stars > 4
                                  ? 'fill-[#FBDD37]'
                                  : 'fill-[#CAD3E2]'
                              }`}
                            />
                          </svg>
                        </div>
                        <div className="review-text text-text-color mr-6">
                          {item.review}
                        </div>
                      </div>
                      <div className="review-actions flex flex-col">
                        {/*
                         * Edit
                         */}
                        <button
                          className="bg-success p-2 rounded-lg"
                          onClick={() => {
                            editReview(item.id, item.stars, item.review)
                          }}>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_1528_11547)">
                              <path
                                d="M18.656 0.930072L6.46402 13.1221C5.99836 13.5852 5.6292 14.1361 5.3779 14.7429C5.1266 15.3497 4.99817 16.0003 5.00002 16.6571V18.0001C5.00002 18.2653 5.10538 18.5196 5.29291 18.7072C5.48045 18.8947 5.7348 19.0001 6.00002 19.0001H7.34302C7.99978 19.0019 8.65039 18.8735 9.25718 18.6222C9.86396 18.3709 10.4149 18.0017 10.878 17.5361L23.07 5.34407C23.6544 4.75824 23.9826 3.96455 23.9826 3.13707C23.9826 2.3096 23.6544 1.5159 23.07 0.930072C22.4757 0.361966 21.6852 0.0449219 20.863 0.0449219C20.0408 0.0449219 19.2503 0.361966 18.656 0.930072V0.930072ZM21.656 3.93007L9.46402 16.1221C8.90015 16.6825 8.13803 16.998 7.34302 17.0001H7.00002V16.6571C7.0021 15.8621 7.31759 15.0999 7.87802 14.5361L20.07 2.34407C20.2836 2.14003 20.5676 2.02617 20.863 2.02617C21.1584 2.02617 21.4424 2.14003 21.656 2.34407C21.866 2.55459 21.9839 2.83976 21.9839 3.13707C21.9839 3.43438 21.866 3.71956 21.656 3.93007V3.93007Z"
                                fill="#FFFFFE"
                              />
                              <path
                                d="M23 8.979C22.7348 8.979 22.4804 9.08436 22.2929 9.27189C22.1054 9.45943 22 9.71378 22 9.979V15H18C17.2044 15 16.4413 15.3161 15.8787 15.8787C15.3161 16.4413 15 17.2044 15 18V22H5C4.20435 22 3.44129 21.6839 2.87868 21.1213C2.31607 20.5587 2 19.7957 2 19V5C2 4.20435 2.31607 3.44129 2.87868 2.87868C3.44129 2.31607 4.20435 2 5 2H14.042C14.3072 2 14.5616 1.89464 14.7491 1.70711C14.9366 1.51957 15.042 1.26522 15.042 1C15.042 0.734784 14.9366 0.48043 14.7491 0.292893C14.5616 0.105357 14.3072 0 14.042 0L5 0C3.67441 0.00158786 2.40356 0.528882 1.46622 1.46622C0.528882 2.40356 0.00158786 3.67441 0 5L0 19C0.00158786 20.3256 0.528882 21.5964 1.46622 22.5338C2.40356 23.4711 3.67441 23.9984 5 24H16.343C16.9999 24.0019 17.6507 23.8735 18.2576 23.6222C18.8646 23.3709 19.4157 23.0017 19.879 22.536L22.535 19.878C23.0008 19.4149 23.37 18.864 23.6215 18.2572C23.873 17.6504 24.0016 16.9998 24 16.343V9.979C24 9.71378 23.8946 9.45943 23.7071 9.27189C23.5196 9.08436 23.2652 8.979 23 8.979ZM18.465 21.122C18.063 21.523 17.5547 21.8006 17 21.922V18C17 17.7348 17.1054 17.4804 17.2929 17.2929C17.4804 17.1054 17.7348 17 18 17H21.925C21.8013 17.5535 21.524 18.0609 21.125 18.464L18.465 21.122Z"
                                fill="#FFFFFE"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_1528_11547">
                                <rect width="24" height="24" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                        </button>
                        <button
                          className="bg-red p-2 rounded-lg mt-3"
                          onClick={() => deleteReview(item.id)}>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_1528_11542)">
                              <path
                                d="M21 4H17.9C17.6679 2.87141 17.0538 1.85735 16.1613 1.12872C15.2687 0.40009 14.1522 0.00145452 13 0L11 0C9.8478 0.00145452 8.73132 0.40009 7.83875 1.12872C6.94618 1.85735 6.3321 2.87141 6.1 4H3C2.73478 4 2.48043 4.10536 2.29289 4.29289C2.10536 4.48043 2 4.73478 2 5C2 5.26522 2.10536 5.51957 2.29289 5.70711C2.48043 5.89464 2.73478 6 3 6H4V19C4.00159 20.3256 4.52888 21.5964 5.46622 22.5338C6.40356 23.4711 7.67441 23.9984 9 24H15C16.3256 23.9984 17.5964 23.4711 18.5338 22.5338C19.4711 21.5964 19.9984 20.3256 20 19V6H21C21.2652 6 21.5196 5.89464 21.7071 5.70711C21.8946 5.51957 22 5.26522 22 5C22 4.73478 21.8946 4.48043 21.7071 4.29289C21.5196 4.10536 21.2652 4 21 4ZM11 2H13C13.6203 2.00076 14.2251 2.19338 14.7316 2.55144C15.2381 2.90951 15.6214 3.41549 15.829 4H8.171C8.37858 3.41549 8.7619 2.90951 9.26839 2.55144C9.77487 2.19338 10.3797 2.00076 11 2ZM18 19C18 19.7956 17.6839 20.5587 17.1213 21.1213C16.5587 21.6839 15.7956 22 15 22H9C8.20435 22 7.44129 21.6839 6.87868 21.1213C6.31607 20.5587 6 19.7956 6 19V6H18V19Z"
                                fill="#FFFFFE"
                              />
                              <path
                                d="M10 18C10.2652 18 10.5196 17.8946 10.7071 17.7071C10.8946 17.5196 11 17.2652 11 17V11C11 10.7348 10.8946 10.4804 10.7071 10.2929C10.5196 10.1054 10.2652 10 10 10C9.73478 10 9.48043 10.1054 9.29289 10.2929C9.10536 10.4804 9 10.7348 9 11V17C9 17.2652 9.10536 17.5196 9.29289 17.7071C9.48043 17.8946 9.73478 18 10 18Z"
                                fill="#FFFFFE"
                              />
                              <path
                                d="M14 18C14.2652 18 14.5196 17.8946 14.7071 17.7071C14.8946 17.5196 15 17.2652 15 17V11C15 10.7348 14.8946 10.4804 14.7071 10.2929C14.5196 10.1054 14.2652 10 14 10C13.7348 10 13.4804 10.1054 13.2929 10.2929C13.1054 10.4804 13 10.7348 13 11V17C13 17.2652 13.1054 17.5196 13.2929 17.7071C13.4804 17.8946 13.7348 18 14 18Z"
                                fill="#FFFFFE"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_1528_11542">
                                <rect width="24" height="24" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                        </button>
                      </div>
                    </div>
                  )
                })}
                {!isLoading && data?.length == 0 && (
                  <div className="orders-empty w-full py-6 flex flex-col justify-center items-center">
                    <h3 className="text-center text-lg text-gray-400">
                      لا يوجد عناصر
                    </h3>

                    <svg
                      width="300"
                      height="225"
                      viewBox="0 0 300 225"
                      fill="none"
                      className="mt-4"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M277.863 138.928C277.912 169.66 267.723 199.533 248.907 223.831H28.9567C10.1403 199.533 -0.0479209 169.66 0.000169467 138.928C0.000169467 62.1979 62.2042 0 138.935 0C215.666 0 277.863 62.1979 277.863 138.928Z"
                        fill="white"
                      />
                      <path
                        opacity="0.1"
                        d="M277.863 138.928C277.912 169.66 267.723 199.533 248.907 223.831H28.9567C10.1403 199.533 -0.0479209 169.66 0.000169467 138.928C0.000169467 62.1979 62.2042 0 138.935 0C215.666 0 277.863 62.1979 277.863 138.928Z"
                        fill="#00214D"
                      />
                      <path
                        d="M280.057 1.77686H169.836C159.085 1.77686 150.37 10.492 150.37 21.2427V21.2431C150.37 31.9938 159.085 40.7089 169.836 40.7089H280.057C290.808 40.7089 299.523 31.9938 299.523 21.2431V21.2427C299.523 10.492 290.808 1.77686 280.057 1.77686Z"
                        fill="white"
                      />
                      <path
                        d="M280.057 41.0931H169.835C164.587 41.0685 159.562 38.9663 155.859 35.2463C152.157 31.5263 150.078 26.4914 150.078 21.2428C150.078 15.9943 152.157 10.9593 155.859 7.23936C159.562 3.51938 164.587 1.41717 169.835 1.39255H280.057C282.671 1.38029 285.263 1.88468 287.682 2.87675C290.101 3.86883 292.3 5.32904 294.153 7.17353C296.006 9.01802 297.477 11.2104 298.48 13.6249C299.484 16.0393 300 18.6282 300 21.2428C300 23.8575 299.484 26.4463 298.48 28.8608C297.477 31.2752 296.006 33.4676 294.153 35.3121C292.3 37.1566 290.101 38.6168 287.682 39.6089C285.263 40.601 282.671 41.1053 280.057 41.0931ZM169.835 2.16104C164.775 2.16104 159.922 4.17136 156.343 7.74977C152.765 11.3282 150.754 16.1815 150.754 21.2422C150.754 26.3028 152.765 31.1562 156.343 34.7346C159.922 38.313 164.775 40.3233 169.835 40.3233H280.057C285.117 40.3233 289.971 38.313 293.549 34.7346C297.127 31.1562 299.138 26.3028 299.138 21.2422C299.138 16.1815 297.127 11.3282 293.549 7.74977C289.971 4.17136 285.117 2.16104 280.057 2.16104H169.835Z"
                        fill="#1B2D45"
                      />
                      <path
                        d="M170.731 11.0146L174.012 17.5066L181.211 18.6152L176.061 23.5903L177.362 30.7023L170.898 27.2851L164.502 30.5717L165.657 23.4846L160.404 18.4043L167.582 17.4415L170.731 11.0146Z"
                        fill="#0CDFBF"
                      />
                      <path
                        d="M197.465 11.0146L200.746 17.5066L207.945 18.6152L202.795 23.5903L204.095 30.7023L197.631 27.2851L191.236 30.5717L192.391 23.4846L187.138 18.4043L194.316 17.4415L197.465 11.0146Z"
                        fill="#0CDFBF"
                      />
                      <path
                        d="M224.198 11.0146L227.479 17.5066L234.679 18.6152L229.528 23.5903L230.829 30.7023L224.365 27.2851L217.969 30.5717L219.124 23.4846L213.871 18.4043L221.049 17.4415L224.198 11.0146Z"
                        fill="#0CDFBF"
                      />
                      <path
                        d="M250.93 11.0146L254.212 17.5066L261.411 18.6152L256.26 23.5903L257.561 30.7023L251.097 27.2851L244.702 30.5717L245.857 23.4846L240.604 18.4043L247.782 17.4415L250.93 11.0146Z"
                        fill="#0CDFBF"
                      />
                      <path
                        opacity="0.2"
                        d="M277.665 11.0146L280.946 17.5066L288.145 18.6152L282.995 23.5903L284.296 30.7023L277.831 27.2851L271.436 30.5717L272.591 23.4846L267.338 18.4043L274.516 17.4415L277.665 11.0146Z"
                        fill="#0CDFBF"
                      />
                      <path
                        d="M79.5363 45.9944C79.5363 45.9944 78.35 60.042 75.0953 65.0886C65.7254 79.6169 62.7934 96.0957 71.7628 104.891C80.7323 113.686 138.914 114.836 143.673 95.0565C147.681 78.4039 139.749 76.4653 138.056 62.6391C137.29 56.3834 135.11 43.2554 135.11 43.2554"
                        fill="#0A0A0A"
                      />
                      <path
                        d="M129.182 91.669C129.081 91.6559 127.966 110.152 106.479 108.539C91.6036 107.425 86.2998 91.6415 85.957 91.6415C87.5608 91.3005 90.2619 90.8049 90.974 90.425C93.3166 89.1662 96.2137 87.8567 96.9695 83.3078C97.4061 80.6879 96.6665 67.2735 96.894 64.6196L119.728 69.7161C119.408 75.3182 117.175 88.306 125.002 90.6045C126.306 90.9874 127.709 91.3367 129.182 91.669Z"
                        fill="#E98862"
                      />
                      <path
                        opacity="0.5"
                        d="M120.049 85.2992C119.692 84.1466 119.461 82.9583 119.363 81.7555C119.111 79.0106 119.162 76.2466 119.512 73.5126C114.871 72.8861 110.499 70.9707 106.893 67.9839C106.469 69.5497 104.137 76.3918 104.783 77.8799C106.002 80.691 111.736 85.2992 120.049 85.2992Z"
                        fill="#00214D"
                      />
                      <path
                        d="M116.796 79.5349C107.78 80.2462 96.1975 76.4391 93.9977 64.4097L92.0123 53.5558C89.8125 41.5263 94.347 30.6191 105.673 28.5477C117 26.4763 127.249 34.6794 129.449 46.7088L130.328 51.5145C131.172 56.1302 132.883 78.2656 116.796 79.5349Z"
                        fill="#E98862"
                      />
                      <path
                        d="M120.477 62.8972L120.499 62.8937C120.958 62.8064 121.412 62.6938 121.858 62.5566C122.525 62.3466 123.398 61.9981 123.289 61.3964C123.163 60.7109 119.933 53.6592 119.565 52.8571C119.532 52.7897 119.473 52.738 119.402 52.713C119.331 52.688 119.253 52.6916 119.184 52.723C119.116 52.7544 119.062 52.8113 119.035 52.8815C119.007 52.9517 119.008 53.0298 119.037 53.0994C120.415 56.1026 122.632 61.0397 122.717 61.5012C122.641 61.7143 121.534 62.1125 120.395 62.3221C120.322 62.3363 120.257 62.3781 120.214 62.4389C120.17 62.4997 120.152 62.5748 120.163 62.6487C120.173 62.7225 120.212 62.7895 120.27 62.8358C120.329 62.882 120.403 62.904 120.477 62.8972Z"
                        fill="#00214D"
                      />
                      <path
                        d="M118.771 68.0744C120.617 67.8461 122.385 67.1869 123.93 66.1502C123.995 66.1085 124.04 66.0429 124.057 65.9676C124.073 65.8924 124.059 65.8137 124.017 65.7489C123.976 65.6841 123.91 65.6385 123.835 65.6221C123.759 65.6057 123.681 65.6199 123.616 65.6616C120.272 67.8107 116.961 68.1417 114.04 66.6178C113.981 66.5853 113.92 66.5561 113.858 66.5305C113.785 66.5039 113.705 66.5071 113.635 66.5395C113.565 66.5718 113.511 66.6306 113.485 66.703C113.458 66.7753 113.461 66.8552 113.494 66.9251C113.526 66.995 113.585 67.0493 113.657 67.0758C113.68 67.0854 113.718 67.106 113.771 67.1339C115.307 67.9377 117.048 68.2652 118.771 68.0744Z"
                        fill="#00214D"
                      />
                      <path
                        d="M82.9393 57.2785L93.092 55.4813C100.792 52.4209 103.196 39.1392 103.196 39.1392C103.196 39.1392 112.47 48.289 135.11 43.2567C135.11 43.2567 130.77 11.6995 97.3134 20.6619C86.5254 23.5516 78.7292 34.3571 79.6029 48.8688C80.2151 59.0368 82.9393 57.2785 82.9393 57.2785Z"
                        fill="#0A0A0A"
                      />
                      <path
                        d="M95.3719 67.9459C91.9299 68.5756 88.466 65.4034 87.8364 61.9614C87.2068 58.5193 89.6502 56.1104 93.0922 55.4812L95.2877 57.4631L98.331 67.4045L95.3719 67.9459Z"
                        fill="#E98862"
                      />
                      <path
                        d="M122.097 47.325C122.965 46.7337 123.974 46.3828 125.022 46.3081C125.549 46.267 126.079 46.3056 126.594 46.4225C127.114 46.5381 127.617 46.7185 128.092 46.9595L128.107 46.9665C128.159 46.9902 128.219 46.9926 128.273 46.9731C128.327 46.9536 128.372 46.9137 128.397 46.862C128.423 46.8103 128.427 46.7506 128.41 46.6958C128.392 46.6409 128.354 46.595 128.303 46.5679C127.8 46.2908 127.265 46.0783 126.709 45.9352C126.149 45.7888 125.571 45.7272 124.993 45.7527C123.822 45.7967 122.684 46.1542 121.699 46.7879C121.662 46.8119 121.63 46.843 121.606 46.8792C121.581 46.9154 121.564 46.9561 121.555 46.999C121.546 47.0419 121.546 47.0861 121.554 47.1292C121.562 47.1722 121.578 47.2132 121.602 47.2499C121.627 47.2865 121.658 47.3181 121.694 47.3427C121.73 47.3674 121.771 47.3847 121.814 47.3936C121.856 47.4025 121.901 47.4029 121.944 47.3947C121.987 47.3865 122.028 47.37 122.064 47.3459L122.072 47.3407L122.097 47.325Z"
                        fill="#00214D"
                      />
                      <path
                        d="M112.832 48.4383C111.687 48.1896 110.495 48.2526 109.383 48.6208C108.832 48.7994 108.312 49.0595 107.839 49.3924C107.368 49.7206 106.941 50.1071 106.567 50.5425C106.547 50.5637 106.531 50.5887 106.521 50.616C106.51 50.6432 106.505 50.6723 106.506 50.7015C106.508 50.7605 106.533 50.8163 106.575 50.8569C106.618 50.8974 106.675 50.9193 106.734 50.9176C106.793 50.916 106.849 50.891 106.89 50.8481L106.902 50.8359C107.262 50.4441 107.67 50.0992 108.117 49.8094C108.558 49.5191 109.041 49.2974 109.549 49.1518C110.558 48.8546 111.628 48.8301 112.65 49.0806L112.675 49.0872C112.76 49.1095 112.851 49.0969 112.928 49.0522C113.004 49.0074 113.06 48.9342 113.082 48.8486C113.104 48.7629 113.092 48.6719 113.047 48.5956C113.002 48.5192 112.929 48.4637 112.843 48.4414L112.832 48.4383Z"
                        fill="#00214D"
                      />
                      <path
                        d="M119.146 70.8047C119.48 70.7689 119.811 70.7044 120.135 70.6122C120.209 70.5913 120.272 70.5419 120.31 70.4747C120.348 70.4075 120.357 70.3281 120.336 70.2539C120.315 70.1797 120.266 70.1169 120.199 70.0792C120.131 70.0415 120.052 70.032 119.978 70.0528C118.03 70.6004 116.929 69.8524 116.883 69.8201C116.82 69.7755 116.742 69.7576 116.666 69.7704C116.59 69.7832 116.522 69.8257 116.478 69.8884C116.433 69.9512 116.415 70.0291 116.428 70.1051C116.441 70.181 116.483 70.2488 116.546 70.2934C117.327 70.7593 118.246 70.9399 119.146 70.8047Z"
                        fill="#00214D"
                      />
                      <path
                        d="M93.1011 55.3155C99.9476 52.9052 102.288 48.2476 103.045 44.7645C103.536 42.4415 103.536 40.0416 103.045 37.7185L102.833 37.7726C103.316 40.0655 103.315 42.4339 102.829 44.7261C102.082 48.152 99.7725 52.7341 93.0273 55.1103L93.1011 55.3155Z"
                        fill="#00214D"
                      />
                      <path
                        opacity="0.1"
                        d="M111.355 63.7247C112.839 63.5742 113.953 62.5696 113.843 61.4808C113.732 60.3921 112.44 59.6315 110.955 59.782C109.471 59.9324 108.357 60.937 108.467 62.0258C108.578 63.1146 109.871 63.8752 111.355 63.7247Z"
                        fill="#00214D"
                      />
                      <path
                        opacity="0.1"
                        d="M127.084 59.6595C127.197 60.7467 128.489 61.5065 129.974 61.358C130.231 61.3318 130.484 61.2791 130.729 61.2008C130.821 59.9084 130.847 58.6639 130.83 57.5112C130.42 57.4011 129.994 57.3685 129.572 57.4152C128.088 57.5636 126.975 58.5679 127.084 59.6595Z"
                        fill="#00214D"
                      />
                      <path
                        d="M61.6848 201.565L59.1064 224.396H152.59L151.149 201.421L61.6848 201.565Z"
                        fill="#1B2D45"
                      />
                      <path
                        d="M105.987 103.942C91.1011 103.241 90.4745 90.606 90.4745 90.606C90.4745 90.606 83.0988 91.0829 78.1954 91.7461C70.1682 92.832 61.6586 95.5885 57.7965 102.263C55.0204 107.062 54.3519 107.93 56.8774 135.01C58.706 154.618 61.6848 201.566 61.6848 201.566L151.149 201.422C151.149 201.422 155.13 141.216 155.881 132.389C157.314 115.539 159.577 91.7906 154.774 90.6052C149.178 89.2241 130.913 91.4946 125.005 90.6052C124.905 90.5921 126.259 104.898 105.987 103.942Z"
                        fill="#0CDFBF"
                      />
                      <path
                        d="M264.544 37.8976C264.674 37.6037 264.863 37.3394 265.099 37.1209C265.334 36.9024 265.612 36.7342 265.915 36.6265C266.218 36.5189 266.54 36.4741 266.86 36.4949C267.181 36.5157 267.494 36.6017 267.781 36.7475C268.326 37.0489 268.735 37.5459 268.928 38.138C269.12 38.7302 269.08 39.3732 268.816 39.9371L267.522 42.8587L275.839 35.514C276.75 34.6813 278.4 34.2416 279.267 35.3245C279.627 35.7194 279.843 36.2248 279.879 36.7582C279.915 37.2916 279.769 37.8215 279.465 38.2613C279.476 38.2682 279.486 38.2764 279.495 38.2858C279.5 38.2933 279.505 38.3012 279.509 38.3094L280.913 37.0252C281.172 36.789 281.48 36.6131 281.814 36.5102C282.149 36.4073 282.502 36.3799 282.849 36.43C283.196 36.4802 283.527 36.6065 283.819 36.8C284.111 36.9936 284.356 37.2494 284.537 37.5492C284.816 38.0336 284.919 38.5994 284.828 39.1509C284.738 39.7024 284.459 40.2057 284.04 40.5755L283.443 41.1218C283.451 41.1471 283.455 41.1738 283.453 41.2003C283.937 40.9271 284.502 40.8332 285.049 40.9353C285.596 41.0373 286.089 41.3288 286.442 41.7584C286.82 42.2531 286.996 42.8728 286.934 43.4924C286.873 44.1119 286.578 44.6849 286.11 45.0956L284.642 46.4387C284.676 46.4343 284.711 46.4382 284.744 46.45C284.777 46.4619 284.806 46.4813 284.83 46.5068C284.856 46.5384 284.873 46.5771 284.878 46.6182C285.369 46.497 285.885 46.5359 286.351 46.7292C286.818 46.9225 287.21 47.2599 287.471 47.6923C287.75 48.1767 287.853 48.7425 287.762 49.294C287.672 49.8456 287.394 50.3488 286.975 50.7186L274.455 62.1712C272.703 63.7733 270.513 64.8157 268.165 65.1648C265.817 65.5139 263.418 65.1539 261.276 64.1309L254.38 60.8369L264.544 37.8976Z"
                        fill="#E98862"
                      />
                      <g opacity="0.2">
                        <path
                          d="M271.271 45.5737C271.31 45.6158 271.363 45.6409 271.421 45.6437C271.478 45.6464 271.534 45.6267 271.576 45.5886L279.477 38.5941C279.498 38.5751 279.516 38.552 279.529 38.5262C279.541 38.5004 279.549 38.4724 279.55 38.4438C279.552 38.4152 279.548 38.3865 279.539 38.3594C279.529 38.3323 279.515 38.3073 279.496 38.2858C279.477 38.2643 279.454 38.2468 279.428 38.2342C279.402 38.2217 279.374 38.2143 279.346 38.2126C279.317 38.2108 279.288 38.2147 279.261 38.2241C279.234 38.2334 279.209 38.248 279.188 38.267L271.284 45.262C271.263 45.281 271.245 45.3041 271.233 45.3299C271.22 45.3557 271.213 45.3837 271.211 45.4123C271.21 45.4409 271.214 45.4696 271.223 45.4967C271.232 45.5238 271.247 45.5488 271.266 45.5702L271.271 45.5737Z"
                          fill="#00214D"
                        />
                      </g>
                      <g opacity="0.2">
                        <path
                          d="M274.26 49.1433C274.299 49.1854 274.352 49.2106 274.41 49.2135C274.467 49.2163 274.523 49.1966 274.566 49.1586L283.383 41.3519C283.404 41.3329 283.422 41.3099 283.434 41.2841C283.447 41.2583 283.454 41.2303 283.456 41.2017C283.458 41.1731 283.454 41.1444 283.444 41.1173C283.435 41.0901 283.42 41.0651 283.401 41.0437C283.362 41.002 283.308 40.9769 283.251 40.9735C283.194 40.9701 283.137 40.9887 283.093 41.0253L274.276 48.8316C274.254 48.8506 274.237 48.8737 274.224 48.8995C274.212 48.9253 274.204 48.9533 274.203 48.9819C274.201 49.0105 274.205 49.0392 274.214 49.0663C274.224 49.0934 274.238 49.1184 274.257 49.1398L274.26 49.1433Z"
                          fill="#00214D"
                        />
                      </g>
                      <g opacity="0.2">
                        <path
                          d="M277.475 53.028C277.513 53.0701 277.567 53.0953 277.624 53.0982C277.681 53.101 277.737 53.0813 277.78 53.0433L284.814 46.8164C284.857 46.7779 284.884 46.7238 284.887 46.666C284.891 46.6081 284.871 46.5513 284.833 46.5079C284.794 46.4645 284.74 46.4382 284.682 46.4347C284.624 46.4312 284.567 46.4509 284.524 46.4893L277.491 52.7162C277.469 52.7352 277.452 52.7583 277.439 52.7841C277.427 52.8099 277.419 52.8379 277.417 52.8665C277.416 52.8951 277.42 52.9238 277.429 52.9509C277.438 52.978 277.453 53.003 277.472 53.0245L277.475 53.028Z"
                          fill="#00214D"
                        />
                      </g>
                      <path
                        d="M256.57 45.9944L209.157 84.833L143.526 90.7538C139.317 91.1065 135.407 93.072 132.614 96.2396C129.82 99.4072 128.358 103.532 128.534 107.752C128.709 111.972 130.509 115.961 133.557 118.885C136.604 121.809 140.664 123.443 144.888 123.445C145.35 123.445 145.816 123.425 146.285 123.385L217.068 117.029C220.421 116.745 223.605 115.435 226.187 113.278L275.841 69.2266L256.57 45.9944Z"
                        fill="#0CDFBF"
                      />
                      <path
                        d="M261.561 35.6137L271.435 32.8463L274.933 23.1909L280.409 31.6718L290.6 31.8128L284.11 39.8216L286.911 49.5644L277.424 46.0328L268.964 51.913L269.59 41.7219L261.561 35.6137Z"
                        fill="#0CDFBF"
                      />
                      <path
                        d="M267.085 43.9499L268.816 39.9372C269.08 39.3732 269.12 38.7302 268.928 38.1381C268.735 37.5459 268.325 37.0489 267.781 36.7475C267.494 36.6017 267.181 36.5157 266.86 36.4949C266.54 36.4742 266.218 36.5189 265.915 36.6266C265.612 36.7342 265.334 36.9024 265.099 37.1209C264.863 37.3395 264.674 37.6037 264.544 37.8976"
                        fill="#E98862"
                      />
                      <path
                        d="M143.299 164.356C143.252 164.976 142.971 165.556 142.512 165.976C142.053 166.397 141.452 166.627 140.829 166.62L138.84 166.644C138.868 166.664 138.891 166.69 138.907 166.72C138.923 166.751 138.932 166.784 138.932 166.819C138.933 166.861 138.92 166.902 138.896 166.935C139.342 167.172 139.701 167.545 139.919 168.001C140.138 168.456 140.205 168.97 140.111 169.466C139.995 170.012 139.693 170.502 139.258 170.853C138.823 171.203 138.28 171.393 137.721 171.391L120.758 171.574C118.384 171.601 116.056 170.918 114.074 169.613C112.091 168.308 110.543 166.441 109.629 164.25L106.682 157.195L115.709 155.274L115.925 153.024L129.555 146.875C129.848 146.743 130.165 146.672 130.486 146.666C130.808 146.66 131.127 146.72 131.424 146.842C131.722 146.963 131.991 147.144 132.217 147.373C132.442 147.603 132.618 147.875 132.735 148.175C132.94 148.763 132.914 149.407 132.663 149.976C132.411 150.546 131.953 150.999 131.381 151.244L128.466 152.557L139.562 152.632C140.798 152.618 142.32 153.39 142.244 154.772C142.25 155.306 142.075 155.828 141.746 156.25C141.418 156.672 140.956 156.971 140.436 157.096C140.436 157.11 140.439 157.119 140.44 157.133L140.433 157.158L142.336 157.138C142.687 157.134 143.034 157.208 143.352 157.355C143.671 157.501 143.953 157.717 144.178 157.986C144.403 158.255 144.566 158.57 144.654 158.91C144.742 159.249 144.754 159.604 144.688 159.949C144.573 160.495 144.273 160.985 143.838 161.335C143.404 161.685 142.862 161.874 142.303 161.87L141.49 161.882C141.478 161.903 141.465 161.924 141.45 161.943C141.992 162.064 142.475 162.372 142.813 162.814C143.151 163.255 143.323 163.801 143.299 164.356Z"
                        fill="#E98862"
                      />
                      <g opacity="0.2">
                        <path
                          d="M129.449 157.082C129.45 157.139 129.474 157.194 129.515 157.234C129.555 157.274 129.61 157.296 129.668 157.296L140.22 157.351C140.278 157.351 140.333 157.328 140.374 157.287C140.415 157.246 140.438 157.191 140.438 157.133C140.438 157.075 140.415 157.02 140.374 156.979C140.333 156.938 140.278 156.915 140.22 156.915L129.668 156.86C129.61 156.86 129.554 156.883 129.513 156.924C129.472 156.964 129.449 157.02 129.449 157.078V157.082Z"
                          fill="#00214D"
                        />
                      </g>
                      <g opacity="0.2">
                        <path
                          d="M129.297 161.735C129.298 161.792 129.321 161.847 129.362 161.887C129.403 161.927 129.458 161.95 129.515 161.95L141.291 162.011C141.349 162.011 141.405 161.988 141.446 161.947C141.487 161.906 141.51 161.851 141.51 161.793C141.508 161.736 141.484 161.681 141.444 161.641C141.403 161.6 141.349 161.576 141.291 161.575L129.515 161.513C129.457 161.513 129.402 161.536 129.361 161.577C129.32 161.618 129.297 161.674 129.297 161.732V161.735Z"
                          fill="#00214D"
                        />
                      </g>
                      <g opacity="0.2">
                        <path
                          d="M129.103 166.774C129.104 166.831 129.127 166.886 129.168 166.926C129.209 166.966 129.264 166.988 129.321 166.988L138.714 167.037C138.772 167.037 138.828 167.014 138.869 166.973C138.91 166.932 138.933 166.877 138.933 166.819C138.933 166.761 138.91 166.706 138.869 166.665C138.828 166.624 138.772 166.601 138.714 166.601L129.321 166.552C129.263 166.552 129.207 166.575 129.166 166.616C129.126 166.657 129.103 166.712 129.103 166.77V166.774Z"
                          fill="#00214D"
                        />
                      </g>
                      <path
                        d="M119.881 176.623L48.8549 189.275C45.8709 190.067 42.7238 189.999 39.7766 189.079C36.8295 188.16 34.2024 186.426 32.1984 184.077C30.1945 181.729 28.8953 178.861 28.4508 175.806C28.0064 172.751 28.4347 169.632 29.6865 166.81L55.5525 106.641C59.2203 98.3749 68.2853 94.6464 76.5505 98.3137C80.5199 100.075 83.6273 103.34 85.189 107.392C86.7507 111.444 86.6389 115.951 84.878 119.92L69.6882 155.927L117.189 149.616L119.881 176.623Z"
                        fill="#0CDFBF"
                      />
                      <path
                        d="M61.0954 187.396C61.1158 187.396 61.1361 187.393 61.1556 187.387L119.941 176.833C119.996 176.817 120.042 176.78 120.07 176.731C120.098 176.682 120.106 176.623 120.092 176.568L117.401 148.687C117.386 148.632 117.351 148.584 117.302 148.555C117.253 148.525 117.194 148.516 117.139 148.529L70.0814 155.613L85.0716 120.015C85.0934 119.963 85.0944 119.905 85.0746 119.853C85.0548 119.8 85.0155 119.758 84.9651 119.733C84.9146 119.709 84.8567 119.705 84.8034 119.722C84.7501 119.739 84.7055 119.776 84.6787 119.826L69.4919 155.833C69.4745 155.87 69.4679 155.911 69.4728 155.951C69.4777 155.992 69.4939 156.031 69.5195 156.062C69.5452 156.094 69.5793 156.118 69.618 156.132C69.6567 156.145 69.6983 156.147 69.7382 156.138L117.032 149L119.615 176.47L61.0347 186.969C60.9839 186.983 60.9401 187.016 60.9113 187.06C60.8825 187.104 60.8707 187.157 60.8781 187.21C60.8855 187.262 60.9116 187.31 60.9516 187.344C60.9915 187.379 61.0426 187.397 61.0954 187.397V187.396Z"
                        fill="#00214D"
                      />
                      <path
                        d="M63.8093 156.931C63.8249 156.931 63.8404 156.929 63.8556 156.926L69.7349 156.14C69.7632 156.134 69.79 156.123 69.8138 156.107C69.8376 156.09 69.858 156.069 69.8737 156.045C69.8894 156.021 69.9002 155.994 69.9053 155.966C69.9105 155.937 69.91 155.908 69.9039 155.88C69.8977 155.852 69.8861 155.825 69.8695 155.801C69.853 155.778 69.832 155.757 69.8076 155.742C69.7832 155.727 69.7561 155.716 69.7276 155.711C69.6992 155.706 69.6701 155.707 69.6419 155.714L63.7626 156.499C63.7102 156.511 63.6639 156.542 63.6326 156.585C63.6014 156.629 63.5873 156.683 63.5931 156.736C63.5988 156.789 63.6241 156.839 63.664 156.875C63.7039 156.911 63.7556 156.931 63.8093 156.931V156.931Z"
                        fill="#00214D"
                      />
                      <path
                        d="M156.712 122.721C156.767 122.722 156.82 122.701 156.861 122.664C156.901 122.626 156.926 122.575 156.93 122.52L157.903 109.928C157.908 109.87 157.889 109.813 157.851 109.769C157.813 109.725 157.76 109.697 157.702 109.693C157.644 109.688 157.587 109.707 157.542 109.745C157.498 109.783 157.471 109.836 157.467 109.894L156.494 122.487C156.489 122.544 156.508 122.601 156.546 122.645C156.583 122.689 156.637 122.716 156.695 122.721C156.7 122.721 156.706 122.722 156.712 122.721Z"
                        fill="#00214D"
                      />
                      <path
                        d="M126.949 51.4553C127.733 51.4553 128.368 50.8204 128.368 50.0371C128.368 49.2538 127.733 48.6189 126.949 48.6189C126.166 48.6189 125.531 49.2538 125.531 50.0371C125.531 50.8204 126.166 51.4553 126.949 51.4553Z"
                        fill="#00214D"
                      />
                      <path
                        d="M112.573 53.884C113.357 53.884 113.992 53.2491 113.992 52.4658C113.992 51.6826 113.357 51.0476 112.573 51.0476C111.79 51.0476 111.155 51.6826 111.155 52.4658C111.155 53.2491 111.79 53.884 112.573 53.884Z"
                        fill="#00214D"
                      />
                      <path
                        d="M26.2511 224.833H251.613C251.729 224.833 251.84 224.786 251.922 224.705C252.004 224.623 252.05 224.512 252.05 224.396C252.05 224.28 252.004 224.169 251.922 224.087C251.84 224.005 251.729 223.959 251.613 223.959H26.2511C26.1353 223.959 26.0242 224.005 25.9423 224.087C25.8605 224.169 25.8145 224.28 25.8145 224.396C25.8145 224.512 25.8605 224.623 25.9423 224.705C26.0242 224.786 26.1353 224.833 26.2511 224.833Z"
                        fill="#292A2E"
                      />
                    </svg>
                  </div>
                )}
              </div>
              {user && (
                <div className="add-review mt-10">
                  <h1 className="text-xl font-semibold">اكتب تعليقك الخاص</h1>
                  <p className="font-medium mt-2">تقييمك للكتاب</p>
                  <div className="ratings-star mt-1">
                    <StarRatings
                      rating={reviewStar}
                      starRatedColor="#FBDD37"
                      starHoverColor="#FBDD37"
                      starDimension="21px"
                      starSpacing="2"
                      changeRating={(newRating: number) => {
                        setReviewStar(newRating)
                      }}
                      numberOfStars={5}
                      name="rating"
                    />
                  </div>
                  <form
                    onSubmit={e => {
                      e.preventDefault()
                      if (!isEditing) {
                        handleCreate()
                      } else {
                        handleUpdate()
                      }
                    }}>
                    <textarea
                      className="rounded-md border border-gray-300 w-full mt-5 py-2 pb-6 text-sm placeholder-gray-400"
                      rows={5}
                      value={review}
                      onChange={e => {
                        setReview(e.target.value)
                      }}
                      placeholder="اكتب تقييمك للكتاب هنا"></textarea>
                    <div className="flex items-center">
                      <div className="text-gray-400 text-sm">
                        {280 - wordCount} حرفًا متبقيًا
                      </div>

                      {isEditing && (
                        <a
                          href="#"
                          className="text-sm mr-2 text-gray-400"
                          onClick={e => {
                            e.preventDefault()
                            setReview('')
                            setReviewStar(0)
                            setIsEditing(undefined)
                          }}>
                          (توقف عن التحرير)
                        </a>
                      )}
                    </div>
                    <div className="btn lg:max-w-sm lg:mx-auto">
                      <Button
                        type={'submit'}
                        bg={'primary'}
                        isLoading={
                          mutation.isLoading ||
                          UpdateMutation.isLoading ||
                          DeleteMutation.isLoading
                        }
                        classes={[
                          'mt-4',
                          'py-3',
                          'bg-primary',
                          'rounded-md',
                          'w-full',
                        ]}>
                        نشر
                      </Button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
          <div className="products container-fluid mx-auto mt-16 lg:mt-24">
            <h2 className="text-success font-semibold text-3xl">
              منتجات ذات صلة
            </h2>
            <div className="products-list mt-6 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6">
              {related_products?.map((related: any, index: number) => {
                return (
                  <ProductItem
                    product={related}
                    key={index}
                    gender={gender}
                    name={name}
                  />
                )
              })}
            </div>
            <div className="browse-btn text-center">
              <Link
                href="/products"
                className="inline-block mt-6 border-2 border-primary text-primary text-center font-semibold rounded-lg px-12 py-[.9rem]">
                استعراض المزيد
              </Link>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
